import { Box, CircularProgress, webPalette } from '@munifocus/ui';
import { useTranslation } from 'react-i18next';
import { SiteLayout } from './SiteLayout';

/**
 * Shown while a lazily-loaded route chunk downloads.
 *
 * Rendered inside {@link SiteLayout} on purpose: every route renders its own
 * SiteLayout, so repeating it here keeps the header and footer *on screen*
 * across the swap instead of the page going blank down to the spinner. They are
 * not preserved across it — Suspense unmounts this subtree and the resolved
 * route mounts its own SiteLayout — but both render from the main chunk, so the
 * chrome is painted for the whole download rather than only after it. (Hoisting
 * a single SiteLayout above the Suspense boundary would genuinely keep it
 * mounted; that means unwrapping all eighteen routes, so it is left alone here.)
 *
 * The spinner fades in after a short delay — on a warm cache the chunk resolves
 * in a few milliseconds, and a spinner that flashes for one frame reads as a
 * glitch.
 */
export function RouteFallback() {
  const { t } = useTranslation();

  return (
    <SiteLayout>
      <Box
        role="status"
        aria-live="polite"
        aria-label={t('web.common.loading')}
        sx={{
          minHeight: '60vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          opacity: 0,
          animation: 'mf-fadeIn 200ms ease-out 250ms forwards',
          '@keyframes mf-fadeIn': { to: { opacity: 1 } },
          '@media (prefers-reduced-motion: reduce)': { animation: 'none', opacity: 1 },
        }}
      >
        <CircularProgress size={28} sx={{ color: webPalette.teal }} />
      </Box>
    </SiteLayout>
  );
}
